import type { ZodType } from "zod";
import z from "zod";

import { IsoDateSchema, type IsoDateUnbranded } from "@thalamos/common";

import {
  SubmissionDocumentSchema,
  type SubmissionDocumentUnbranded,
} from "../../assessments/document.js";
import {
  PresentationSchema,
  type PresentationUnbranded,
} from "../../assessments/presentation.js";
import { isIdentifier } from "../../assessments/spelling.js";
import {
  SubmissionNumberSchema,
  type SubmissionNumberUnbranded,
} from "../../types/assessment.js";
import { UserIdSchema, type UserIdUnbranded } from "../../types/user.js";
import type { PreviousCommentUnbranded } from "./carryOver.js";
import type { FormContext } from "./checks.js";

/** A context as it travels and is stored: the facts a draft's rules are judged against. */
export const FormContextSchema = z.object({
  now: z.iso.datetime({ offset: true }),
}) satisfies ZodType<FormContext>;

export type AnswerStampUnbranded = {
  savedAt: Date;
  savedByUserId: UserIdUnbranded;
};

export const AnswerStampSchema = z.object({
  savedAt: z.coerce.date(),
  savedByUserId: UserIdSchema,
}) satisfies ZodType<AnswerStampUnbranded>;

/** Who last saved a change to one part of a draft, and when. */
export type AnswerStamp = z.infer<typeof AnswerStampSchema>;

export const AnswerStampsSchema = z.record(
  z.string(),
  AnswerStampSchema,
) satisfies ZodType<Record<string, AnswerStampUnbranded>>;

/**
 * A stamp for each response a draft holds, a comment's included, keyed by its
 * JSON pointer (`/group/item/response`): the latest save that changed it. A
 * stamp names the change, never its value.
 */
export type AnswerStamps = z.infer<typeof AnswerStampsSchema>;

/**
 * The form's work in progress on one assessment.
 *
 * `document` is in the shape a submission takes, but may be incomplete or wrong
 * until it is submitted. `context` holds the facts its answers are judged
 * against: as stored, those of its last save; as the server hands it back, a
 * snapshot taken for that request. The browser never supplies one. `answerStamps` says who saved each answer, and when. `carriedRatings`
 * holds the ratings the assessment carried over from the one it followed when
 * its first draft started, empty where none of them qualified, or is null
 * where it carried nothing over. `openedGroups` names the groups its owner has
 * opened, in key order; an assessment that carried any rating over is not ready until every
 * group it includes is among them.
 *
 * Written out beside the schema because tsoa cannot follow `z.infer`.
 */
export type FormDraftUnbranded = {
  document: SubmissionDocumentUnbranded;
  context: { now: string };
  savedAt: Date;
  answerStamps: Record<string, AnswerStampUnbranded>;
  carriedRatings: SubmissionDocumentUnbranded | null;
  openedGroups: string[];
};

export const FormDraftSchema = z
  .object({
    document: SubmissionDocumentSchema,
    context: FormContextSchema,
    savedAt: z.coerce.date(),
    answerStamps: AnswerStampsSchema,
    carriedRatings: SubmissionDocumentSchema.nullable(),
    openedGroups: z.array(z.string()),
  })
  .brand<"haven-common:FormDraft">() satisfies ZodType<FormDraftUnbranded>;

export type FormDraft = z.infer<typeof FormDraftSchema>;

export type StartFormDraftRequestUnbranded = {
  fromSubmission?: SubmissionNumberUnbranded | undefined;
  carryOver?: boolean | undefined;
};

export const StartFormDraftRequestSchema = z
  .object({
    fromSubmission: SubmissionNumberSchema.optional(),
    carryOver: z.boolean().optional(),
  })
  .refine(
    (start) => start.fromSubmission === undefined || start.carryOver !== true,
    "A draft starts from a submission or with carried ratings, not both",
  )
  .brand<"haven-common:StartFormDraftRequest">() satisfies ZodType<StartFormDraftRequestUnbranded>;

/**
 * What starting a draft states: nothing, for an empty one; the number of the
 * assessment's submission to start from; or that it starts with the ratings
 * of the finished assessment its assessment followed.
 */
export type StartFormDraftRequest = z.infer<typeof StartFormDraftRequestSchema>;

export type SaveFormDraftRequestUnbranded = {
  document: SubmissionDocumentUnbranded;
};

export const SaveFormDraftRequestSchema = z
  .object({ document: SubmissionDocumentSchema })
  .brand<"haven-common:SaveFormDraftRequest">() satisfies ZodType<SaveFormDraftRequestUnbranded>;

/** What saving a draft states: the document as it now stands, replacing the last save. */
export type SaveFormDraftRequest = z.infer<typeof SaveFormDraftRequestSchema>;

export type SubmitFormDraftRequestUnbranded = {
  document: SubmissionDocumentUnbranded;
  assessedOn: IsoDateUnbranded;
  presentation: PresentationUnbranded;
  /**
   * Who the form told the assessor a definitive assessment goes to for
   * approval, so it is refused rather than sent to somebody else once its
   * approver changes.
   */
  approverUserId?: UserIdUnbranded | undefined;
};

export const SubmitFormDraftRequestSchema = z
  .object({
    document: SubmissionDocumentSchema,
    assessedOn: z.iso.date("assessment.assessedOn.invalid").pipe(IsoDateSchema),
    presentation: PresentationSchema,
    approverUserId: UserIdSchema.optional(),
  })
  .brand<"haven-common:SubmitFormDraftRequest">() satisfies ZodType<SubmitFormDraftRequestUnbranded>;

/**
 * What submitting a draft states: the document as finally answered, the day it
 * describes the young person as of, and evidence of what the form showed.
 */
export type SubmitFormDraftRequest = z.infer<
  typeof SubmitFormDraftRequestSchema
>;

/** What opening a group states: which one, by its key. */
export type OpenGroupRequestUnbranded = {
  group: string;
};

export const OpenGroupRequestSchema = z
  .object({ group: z.string().refine(isIdentifier, "Not a group's key") })
  .brand<"haven-common:OpenGroupRequest">() satisfies ZodType<OpenGroupRequestUnbranded>;

export type OpenGroupRequest = z.infer<typeof OpenGroupRequestSchema>;

/**
 * The groups opened, for `progressOf` to hold every other group back, or
 * `undefined` where none is held back: only an assessment that carried at least
 * one rating over starts with answers its assessor has not looked at.
 */
export const openedWhereRequired = (draft: {
  carriedRatings: SubmissionDocumentUnbranded | null;
  openedGroups: readonly string[];
}): ReadonlySet<string> | undefined =>
  draft.carriedRatings === null ||
  Object.keys(draft.carriedRatings).length === 0
    ? undefined
    : new Set(draft.openedGroups);

/**
 * Last time's comments, for an assessment that carried ratings over, and when
 * the assessment they came from was approved or completed.
 *
 * Written out beside the schema because tsoa cannot follow `z.infer`.
 */
export type PreviousCommentsUnbranded = {
  finishedAt: Date;
  comments: PreviousCommentUnbranded[];
};

/**
 * What a draft's previous comments come to: none where nothing was carried
 * over, or where the reader may not read what the earlier assessment says.
 */
export type PreviousCommentsAnswerUnbranded = {
  previous: PreviousCommentsUnbranded | null;
};

export const PreviousCommentsAnswerSchema = z.object({
  previous: z
    .object({
      finishedAt: z.coerce.date(),
      comments: z.array(
        z.object({ group: z.string(), item: z.string(), comment: z.string() }),
      ),
    })
    .nullable(),
}) satisfies ZodType<PreviousCommentsAnswerUnbranded>;

export type PreviousCommentsAnswer = z.infer<
  typeof PreviousCommentsAnswerSchema
>;

/**
 * How far an assessment's work has got, in domains: those completed and those
 * that count, as `progressOf` reckons them.
 *
 * Written out beside the schema because tsoa cannot follow `z.infer`.
 */
export type FormProgressUnbranded = {
  completed: number;
  included: number;
};

export const FormProgressSchema = z.object({
  completed: z.number().int().nonnegative(),
  included: z.number().int().nonnegative(),
}) satisfies ZodType<FormProgressUnbranded>;

export type FormProgress = z.infer<typeof FormProgressSchema>;
